import { memo, useCallback, useMemo, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import { Box, SxProps, Typography } from "@mui/material";

import { Poem } from "@/extensions/obituary/types";

export interface PoemCardProps extends Omit<Bananas.CardProps, "onClick"> {
  selected?: boolean;
  poem: Poem;
  onClick: (symbol: Poem) => void;
}

const styles: Record<string, SxProps> = {
  paper: {
    cursor: "pointer",
    "&:hover": { borderColor: "text.secondary" },
    overflow: "hidden",
    position: "relative",
    pb: 4,
  },

  selected: {
    outline: "1px solid",
    outlineColor: "primary.main",
    borderColor: "primary.main",
  },

  root: {
    height: 180,
    width: "100%",
    overflow: "hidden",
  },

  expandedRoot: {
    minHeight: 180,
    height: "100% !important",
  },

  text: {
    fontSize: 14,
    whiteSpace: "pre-wrap",
  },

  shadow: {
    position: "absolute",
    bottom: 0,
    mb: 4,
    left: 0,
    width: "100%",
    height: 80,
  },

  footer: {
    p: 1,
    bgcolor: "background.default",
    mt: "auto",
    borderTop: "1px solid",
    borderColor: "divider",
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
  },
};

const PoemCard: React.FC<PoemCardProps> = ({
  onClick,
  selected,
  poem,
  sx,
  ...props
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleOnClick = useCallback(() => {
    setExpanded(true);
    onClick(poem);
  }, [onClick, poem]);

  const hasAuthor = useMemo(
    () => poem?.author != null && poem.author !== "",
    [poem],
  );

  return (
    <Bananas.Card
      role="button"
      sx={
        {
          ...styles.paper,
          ...(expanded ? styles.expandedRoot : styles.root),
        } as SxProps
      }
      onClick={handleOnClick}
      {...props}
    >
      <Bananas.CardContent>
        <Typography sx={styles.text} variant="body1">
          {poem.text}
        </Typography>
      </Bananas.CardContent>
      {!expanded && (
        <Box
          sx={(theme) => ({
            ...(styles.shadow as object),
            background: `linear-gradient(0deg, ${theme.palette.background.paper}, rgba(255, 255, 255, 0))`,
          })}
        />
      )}
      <Box sx={styles.footer}>
        <Typography sx={styles.title} variant="caption">
          {poem?.reference} {hasAuthor && `• ${poem?.author}`}
        </Typography>
      </Box>
    </Bananas.Card>
  );
};

export default memo(PoemCard);
